import { useId, useRef, useState, type FocusEvent, type KeyboardEvent, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import styles from './NavDropdown.module.css';

export interface NavDropdownItem {
  label: string;
  href: string;
}

interface NavDropdownProps {
  label: string;
  items: NavDropdownItem[];
  onItemSelect?: () => void;
}

const isExternalHttpLink = (href: string): boolean => /^https?:\/\//.test(href);

const normalizePath = (path: string): string => path.replace(/\/+$/, '') || '/';

export default function NavDropdown({ label, items, onItemSelect }: NavDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const location = useLocation();

  const close = () => setIsOpen(false);

  const closeAndNotify = () => {
    close();
    onItemSelect?.();
  };

  // Cerrar al cambiar de ruta. Se compara con la ruta del render anterior en
  // vez de usar un efecto: es el patron recomendado para ajustar estado cuando
  // cambia una entrada, y evita el render extra con el menu aun abierto.
  const [renderedPath, setRenderedPath] = useState(location.pathname);
  if (renderedPath !== location.pathname) {
    setRenderedPath(location.pathname);
    setIsOpen(false);
  }

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!rootRef.current?.contains(event.relatedTarget as Node | null)) {
      close();
    }
  };

  const handleInternalItemClick = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    closeAndNotify();

    if (normalizePath(location.pathname) === normalizePath(href)) {
      event.preventDefault();
      window.location.reload();
    }
  };

  const focusFirstItem = () => {
    requestAnimationFrame(() => menuRef.current?.querySelector<HTMLElement>('a')?.focus());
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      close();
      triggerRef.current?.focus();
    }
  };

  return (
    <div className={styles.navdd} ref={rootRef} onBlur={handleBlur} onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setIsOpen(true);
            focusFirstItem();
          }
        }}
      >
        {label}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <div
        ref={menuRef}
        id={menuId}
        className={isOpen ? `${styles.menu} ${styles.menuOpen}` : styles.menu}
        hidden={!isOpen}
      >
        {items.map((item) =>
          isExternalHttpLink(item.href) ? (
            <a
              key={item.href}
              className={styles.item}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeAndNotify}
            >
              {item.label}
            </a>
          ) : (
            (() => {
              const isActive = normalizePath(location.pathname) === normalizePath(item.href);

              return (
            <Link
              key={item.href}
              className={isActive ? `${styles.item} ${styles.itemActive}` : styles.item}
              to={item.href}
              aria-current={isActive ? 'page' : undefined}
              onClick={(event) => handleInternalItemClick(event, item.href)}
            >
              {item.label}
            </Link>
              );
            })()
          ),
        )}
      </div>
    </div>
  );
}
